'use client';
import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import MobilePartnerLayout from '@/components/MobilePartnerLayout';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface PayoutHistory {
  id: string;
  payout_type: string;
  amount: number;
  status: string;
  created_at: string;
  notes: string | null;
}

interface LeaderboardEntry {
  agent_id: string;
  full_name: string;
  agent_code: string | null;
  lead_count: number;
}

const rewardCatalog = [
  { id: 1, name: 'Voucher Belanja Rp 100.000', btp: 1000, category: 'Voucher', icon: '🎁' },
  { id: 2, name: 'Voucher Belanja Rp 250.000', btp: 2500, category: 'Voucher', icon: '🎁' },
  { id: 3, name: 'Pulsa Rp 50.000', btp: 500, category: 'Pulsa', icon: '📱' },
  { id: 4, name: 'Transfer Tunai Rp 500.000', btp: 5000, category: 'Tunai', icon: '💰' },
  { id: 5, name: 'Merchandise Betaland', btp: 800, category: 'Merchandise', icon: '👕' },
  { id: 6, name: 'Liburan 2D1N Bali', btp: 15000, category: 'Travel', icon: '✈️' },
];

const MEDALS = ['🥇', '🥈', '🥉'];
const BTP_RATE = 100;

export default function MobileRewardPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState<'ringkasan' | 'store' | 'leaderboard'>('ringkasan');
  const [history, setHistory] = useState<PayoutHistory[]>([]);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    const fetchHistory = async () => {
      const supabase = createClient();
      setLoading(true);
      const { data } = await supabase
        .from('reward_payouts')
        .select('id, payout_type, amount, status, created_at, notes')
        .eq('recipient_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20);
      if (data) setHistory(data as PayoutHistory[]);
      setLoading(false);
    };
    fetchHistory();
  }, [user]);

  useEffect(() => {
    if (tab !== 'leaderboard') return;
    const fetchLeaderboard = async () => {
      setLoadingLeaderboard(true);
      const supabase = createClient();
      // Get top agents by lead count
      const { data } = await supabase
        .from('leads')
        .select('agent_id, agent_code')
        .not('agent_id', 'is', null);

      if (data) {
        // Count leads per agent
        const countMap: Record<string, { count: number; agent_code: string | null }> = {};
        data.forEach((row: any) => {
          if (!row.agent_id) return;
          if (!countMap[row.agent_id]) countMap[row.agent_id] = { count: 0, agent_code: row.agent_code };
          countMap[row.agent_id].count++;
        });

        // Get agent names
        const agentIds = Object.keys(countMap);
        if (agentIds.length > 0) {
          const { data: profiles } = await supabase
            .from('user_profiles')
            .select('id, full_name, agent_code')
            .in('id', agentIds);

          const profileMap: Record<string, string> = {};
          (profiles || []).forEach((p: any) => { profileMap[p.id] = p.full_name; });

          const sorted = agentIds
            .map((id) => ({
              agent_id: id,
              full_name: profileMap[id] || 'Partner',
              agent_code: countMap[id].agent_code,
              lead_count: countMap[id].count,
            }))
            .sort((a, b) => b.lead_count - a.lead_count)
            .slice(0, 10);

          setLeaderboard(sorted);
        }
      }
      setLoadingLeaderboard(false);
    };
    fetchLeaderboard();
  }, [tab]);

  const totalPaid = history.filter((h) => h.status === 'PAID').reduce((sum, h) => sum + h.amount, 0);
  const totalPending = history.filter((h) => h.status === 'PENDING').reduce((sum, h) => sum + h.amount, 0);
  const totalApproved = history.filter((h) => h.status === 'PROCESSING').reduce((sum, h) => sum + h.amount, 0);
  const currentBTP = Math.floor(totalPaid / BTP_RATE);

  function formatRp(n: number) {
    if (n >= 1_000_000) return `Rp${(n / 1_000_000).toFixed(1)} Jt`;
    return `Rp${n.toLocaleString('id-ID')}`;
  }

  return (
    <MobilePartnerLayout showFloatingButton={false}>
      {/* Header */}
      <div className="bg-primary px-5 pt-12 pb-6">
        <h1 className="text-white font-bold text-lg mb-1">Reward & Komisi</h1>
        <p className="text-white/70 text-xs">Pantau pendapatan dan tukarkan poin Anda</p>
      </div>

      <div className="px-4 -mt-3 space-y-4 pb-4">
        {/* BTP Points Card */}
        <div className="bg-gradient-to-br from-primary to-primary/80 rounded-2xl p-5 text-white shadow-lg">
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-xs font-semibold opacity-70 uppercase tracking-wide">Total Point Anda</p>
              {loading ? (
                <div className="h-10 w-28 bg-white/20 rounded animate-pulse mt-1" />
              ) : (
                <>
                  <p className="text-4xl font-bold mt-1">{currentBTP.toLocaleString('id-ID')} <span className="text-xl font-semibold opacity-80">BTP</span></p>
                  <p className="text-sm opacity-70 mt-0.5">≈ Rp{(currentBTP * BTP_RATE).toLocaleString('id-ID')}</p>
                </>
              )}
            </div>
            <div className="text-3xl">⭐</div>
          </div>
          <button
            onClick={() => setTab('store')}
            className="w-full py-2.5 bg-white/20 hover:bg-white/30 rounded-xl text-sm font-bold transition-all active:scale-95"
          >
            ⭐ Tukarkan Reward
          </button>
        </div>

        {/* Komisi Summary */}
        <div className="bg-card rounded-2xl border border-border p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-bold text-foreground">Ringkasan Komisi</span>
            <button
              onClick={() => setTab('ringkasan')}
              className="text-xs text-primary font-semibold"
            >
              Lihat Semua
            </button>
          </div>
          <div className="space-y-2.5">
            {[
              { label: 'Pending', value: loading ? '...' : formatRp(totalPending), color: 'text-orange', icon: '⏳' },
              { label: 'Diproses', value: loading ? '...' : formatRp(totalApproved), color: 'text-blue-600', icon: '✅' },
              { label: 'Dibayar', value: loading ? '...' : formatRp(totalPaid), color: 'text-green-600', icon: '💰' },
            ].map((item) => (
              <div key={item.label} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                <div className="flex items-center gap-2">
                  <span className="text-base">{item.icon}</span>
                  <span className="text-sm text-muted-foreground">{item.label}</span>
                </div>
                <span className={`text-sm font-bold ${item.color}`}>{item.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-muted p-1 rounded-xl">
          {(['ringkasan', 'store', 'leaderboard'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${tab === t ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`}
            >
              {t === 'ringkasan' ? 'Riwayat' : t === 'store' ? 'Reward Store' : 'Leaderboard'}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {tab === 'ringkasan' && (
          <div className="space-y-2">
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="bg-card rounded-xl border border-border p-4 animate-pulse">
                  <div className="h-4 bg-muted rounded w-2/3 mb-2" />
                  <div className="h-3 bg-muted rounded w-1/3" />
                </div>
              ))
            ) : history.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Icon name="BanknotesIcon" size={36} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm">Belum ada riwayat komisi</p>
              </div>
            ) : (
              history.map((h) => (
                <div key={h.id} className="bg-card rounded-xl border border-border px-4 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-foreground capitalize">{h.payout_type.replace(/_/g, ' ')}</p>
                    <p className="text-xs text-muted-foreground">{new Date(h.created_at).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-bold ${h.status === 'PAID' ? 'text-green-600' : 'text-muted-foreground'}`}>
                      Rp{h.amount.toLocaleString('id-ID')}
                    </p>
                    <p className="text-xs text-muted-foreground capitalize">{h.status}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {tab === 'store' && (
          <div className="grid grid-cols-2 gap-3">
            {rewardCatalog.map((item) => (
              <div key={item.id} className="bg-card rounded-2xl border border-border p-4">
                <div className="text-3xl mb-2">{item.icon}</div>
                <p className="text-xs font-semibold text-foreground leading-tight mb-2">{item.name}</p>
                <div className="flex items-center gap-1 mb-3">
                  <span className="text-yellow-500 text-xs">⭐</span>
                  <span className="text-xs font-bold text-primary">{item.btp.toLocaleString()} BTP</span>
                </div>
                <button
                  disabled={currentBTP < item.btp}
                  className={`w-full py-2 rounded-xl text-xs font-bold transition-all ${
                    currentBTP >= item.btp ? 'bg-primary text-white active:scale-95' : 'bg-muted text-muted-foreground cursor-not-allowed'
                  }`}
                >
                  {currentBTP >= item.btp ? 'Tukar' : 'BTP Kurang'}
                </button>
              </div>
            ))}
          </div>
        )}

        {tab === 'leaderboard' && (
          <div className="space-y-3">
            <div className="bg-card rounded-2xl border border-border overflow-hidden">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <span className="text-sm font-bold text-foreground">Leaderboard Partner</span>
                <span className="text-xs text-muted-foreground">By Total Lead</span>
              </div>
              {loadingLeaderboard ? (
                <div className="p-4 space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-3 animate-pulse">
                      <div className="w-8 h-8 rounded-full bg-muted" />
                      <div className="flex-1 h-4 bg-muted rounded" />
                    </div>
                  ))}
                </div>
              ) : leaderboard.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p className="text-sm">Belum ada data leaderboard</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {leaderboard.map((item, idx) => {
                    const isMe = item.agent_id === user?.id;
                    return (
                      <div
                        key={item.agent_id}
                        className={`flex items-center gap-3 px-4 py-3.5 ${isMe ? 'bg-primary/5' : ''}`}
                      >
                        <span className="text-xl w-8 text-center">
                          {idx < 3 ? MEDALS[idx] : `#${idx + 1}`}
                        </span>
                        <div className="flex-1">
                          <p className={`text-sm font-bold ${isMe ? 'text-primary' : 'text-foreground'}`}>
                            {item.full_name}{isMe ? ' (Anda)' : ''}
                          </p>
                          <p className="text-xs text-muted-foreground">{item.lead_count} Lead</p>
                        </div>
                        {isMe && (
                          <span className="text-xs bg-primary/10 text-primary font-bold px-2 py-0.5 rounded-full">Anda</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </MobilePartnerLayout>
  );
}
